<template>
  <div class="range-input" :class="[`range-input--${size}`, { 'has-error': !!error, 'is-disabled': disabled }]">
    <el-input-number
      :controls="false"
      :value="min == null ? undefined : min"
      :min="minFloor"
      :step="1"
      :disabled="disabled"
      :placeholder="minPlaceholder"
      @input="v => emit('update:min', normalize(v))"
    />
    <span class="range-input__sep" aria-hidden="true">&ndash;</span>
    <el-input-number
      :controls="false"
      :value="max == null ? undefined : max"
      :min="1"
      :step="1"
      :disabled="disabled"
      :placeholder="maxPlaceholder"
      @input="v => emit('update:max', normalize(v))"
    />
    <small v-if="errorMessage" class="range-input__error">{{ errorMessage }}</small>
  </div>
</template>

<script setup lang="ts">
// A min to max whole-number range (participant counts, player bands) in one
// bordered group. Two-way bound through `min.sync` / `max.sync`.
import { computed, type PropType } from 'vue'

const props = defineProps({
  min: { type: Number as PropType<number | null>, default: null },
  max: { type: Number as PropType<number | null>, default: null },
  minFloor: { type: Number, default: 1 },
  size: { type: String as PropType<'sm' | 'md'>, default: 'md' },
  disabled: { type: Boolean, default: false },
  error: { type: [Boolean, String] as PropType<boolean | string>, default: false },
  minPlaceholder: { type: String, default: '' },
  maxPlaceholder: { type: String, default: '' }
})

const emit = defineEmits<{
  (e: 'update:min', value: number | null): void
  (e: 'update:max', value: number | null): void
}>()

const errorMessage = computed(() => (typeof props.error === 'string' ? props.error : ''))

function normalize(v: number | null | undefined): number | null {
  return v == null ? null : v
}
</script>

<script lang="ts">
export default { name: 'RangeInput' }
</script>

<style lang="scss" scoped>
.range-input {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.4285rem;
  // Same fill as the money input beside it
  background: rgba(30, 30, 47, 0.6);
  height: 40px;

  &:focus-within { border-color: #e14eca; }
  &.has-error { border-color: #ec250d; }
  &.is-disabled { opacity: 0.6; }

  // Each half fills the box height and centers its number
  ::v-deep .el-input-number {
    flex: 1;
    min-width: 0;
    width: auto;
    height: 100%;
    line-height: normal;
  }

  ::v-deep .el-input-number .el-input,
  ::v-deep .el-input-number .el-input__inner {
    height: 100%;
  }

  ::v-deep .el-input-number .el-input__inner {
    border: none;
    background: transparent;
    color: white;
    text-align: center;
    padding: 0 4px;
    line-height: normal;
    font-size: 0.875rem;
    font-variant-numeric: tabular-nums;
  }
}

.range-input--sm {
  height: 32px;

  ::v-deep .el-input-number .el-input__inner {
    font-size: 13px;
  }
}

.range-input__sep {
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  flex-shrink: 0;
  user-select: none;
  padding: 0 2px;
}

.range-input__error {
  flex-basis: 100%;
  display: block;
  margin-top: 4px;
  color: #ec250d;
  font-size: 0.75rem;
}
</style>

<style lang="scss">
.white-content .range-input {
  border-color: #cad1d7;
  background: #ffffff;

  &:focus-within { border-color: #e14eca; }

  .el-input-number .el-input__inner { color: #525f7f; }
  .range-input__sep { color: #8898aa; }
}
</style>
